import { useNavigate } from "react-router";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";

import {
  PortfolioCardShell,
  PortfolioCardsList,
  PortfolioMetricTile,
  PortfolioStatusTag,
} from "@/views/Portfolio/shared";
import type { TrialInfo } from "@/views/Portfolio/types";
import { buildTrialSiteUrl } from "../utils/trialNavigation";
import type { SiteTrialEntry } from "../types";

type SummaryTabProps = {
  trialEntries: SiteTrialEntry[];
  trials: TrialInfo[];
  activeTab: string;
};

export default function SummaryTab({
  trialEntries,
  trials,
  activeTab,
}: SummaryTabProps) {
  const navigate = useNavigate();
  const trialSiteHidden = useFeatureFlag(
    FEATURE_FLAGS.TRIAL_SITE_PROFILE_LINKS_HIDDEN,
  );

  const enriched = trialEntries
    .map((entry) => ({
      entry,
      trialInfo: trials.find((t) => t.id === entry.trial_id) ?? null,
    }))
    .filter(({ trialInfo }) => trialInfo !== null);

  if (enriched.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-[13px]">
        No trial data available for this site.
      </div>
    );
  }

  return (
    <PortfolioCardsList>
      {enriched.map(({ entry, trialInfo }) => {
        const info = trialInfo!;
        const enrollPct =
          entry.target != null && entry.target > 0 && entry.enrolled != null
            ? Math.round((entry.enrolled / entry.target) * 100)
            : null;

        const header = (
          <div className="flex h-[45px] items-center justify-between gap-4 border-b border-[#F0F2F4] px-[18px]">
            <div className="flex min-w-0 items-center gap-2.5">
              {trialSiteHidden ? (
                <span className="text-[15px] font-semibold text-[#1F2933] leading-5 truncate">
                  {info.name ?? info.id}
                </span>
              ) : (
                <button
                  type="button"
                  className="text-[15px] font-semibold text-[#1F2933] hover:text-[#0C98D0] leading-5 truncate"
                  onClick={() =>
                    navigate(
                      buildTrialSiteUrl(
                        entry.trial_id,
                        entry.site_id,
                        activeTab,
                      ),
                    )
                  }
                >
                  {info.name ?? info.id}
                </button>
              )}
              <span className="text-[13px] leading-4 text-[#8C8C8C]">
                {entry.trial_id}
              </span>
              {info.phase && (
                <span className="inline-flex shrink-0 items-center h-5 rounded-[4px] bg-[#E8EEF2] px-1.5 text-[13px] leading-5 text-[#48636F] whitespace-nowrap">
                  Phase {info.phase}
                </span>
              )}
              {info.asset && (
                <span className="text-[13px] leading-4 text-[#6B7280]">
                  {info.asset}
                </span>
              )}
            </div>
            <PortfolioStatusTag
              status={entry.status}
              color={entry.statusColor}
            />
          </div>
        );

        const footer = (
          <div className="flex items-center gap-6 px-[17px] py-2 bg-[#FAFBFC]">
            <span className="text-[11px] text-[#8C8C8C]">
              <span className="font-medium text-[#4B5563]">Last visit:</span>{" "}
              {entry.last_monitoring_visit ?? "—"}
            </span>
            <span className="text-[11px] text-[#8C8C8C]">
              <span className="font-medium text-[#4B5563]">Open queries:</span>{" "}
              {entry.open_queries ?? "—"}
            </span>
            <span className="text-[11px] text-[#8C8C8C]">
              <span className="font-medium text-[#4B5563]">
                CRF completion:
              </span>{" "}
              {entry.crf_completion_pct != null
                ? `${entry.crf_completion_pct}%`
                : "—"}
            </span>
          </div>
        );

        return (
          <PortfolioCardShell
            key={entry.trial_id}
            header={header}
            metricColumns={3}
            additionalColumns={1}
            additionalContent={footer}
          >
            <PortfolioMetricTile
              label="Enrolled"
              value={entry.enrolled ?? "—"}
              suffix={entry.target != null ? `/ ${entry.target}` : ""}
              suffixClassName="text-[14px] text-[#2B4752]"
              progress={enrollPct ?? undefined}
              progressTone="blue"
              note={enrollPct != null ? `${enrollPct}% of target` : undefined}
            />
            <PortfolioMetricTile
              label="AE Rate"
              value={entry.ae_rate_pct ?? "—"}
              suffix={entry.ae_rate_pct != null ? "%" : ""}
              note={
                entry.ae_count != null ? `${entry.ae_count} events` : undefined
              }
            />
            <PortfolioMetricTile
              label="Open PDs"
              value={entry.open_pds ?? "—"}
              note={entry.open_pds === 0 ? "None open" : undefined}
            />
          </PortfolioCardShell>
        );
      })}
    </PortfolioCardsList>
  );
}
